import { Controller } from "@hotwired/stimulus"

// Liga/desliga e configura notificações nativas do navegador (Web Notifications API)
// e filtros de categorias permitidas para popup.
// Preferência por dispositivo, guardada em localStorage.
export default class extends Controller {
  static targets = ["toggle", "hint", "statusBadge", "categoryCheck", "panel", "panelToggleBtn"]
  static KEY_ENABLED = "notif_browser_enabled"
  static KEY_CATEGORIES = "notif_browser_categories"

  static DEFAULT_CATEGORIES = [
    "campanha",
    "sistema",
    "warning",
    "error",
    "success",
    "info"
  ]

  connect() {
    this.syncUI()

    this.handlePrefChange = () => this.syncUI()
    window.addEventListener("notif-pref-changed", this.handlePrefChange)
  }

  disconnect() {
    window.removeEventListener("notif-pref-changed", this.handlePrefChange)
  }

  get allowedCategories() {
    const raw = localStorage.getItem(this.constructor.KEY_CATEGORIES)
    if (!raw) return [...this.constructor.DEFAULT_CATEGORIES]
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : [...this.constructor.DEFAULT_CATEGORIES]
    } catch {
      return [...this.constructor.DEFAULT_CATEGORIES]
    }
  }

  syncUI() {
    const isEnabled = localStorage.getItem(this.constructor.KEY_ENABLED) === "1" && this.granted()

    if (this.hasToggleTarget) {
      this.toggleTargets.forEach((t) => {
        t.checked = isEnabled
      })
    }

    if (this.hasCategoryCheckTargets) {
      const allowed = this.allowedCategories
      this.categoryCheckTargets.forEach((chk) => {
        const cat = chk.dataset.category
        chk.checked = allowed.includes(cat)
        chk.disabled = !isEnabled
      })
    }

    this.updateHint()
  }

  toggle(event) {
    const shouldEnable = event.target.checked

    if (!shouldEnable) {
      localStorage.setItem(this.constructor.KEY_ENABLED, "0")
      this.notifyChange()
      this.syncUI()
      return
    }

    if (!("Notification" in window)) {
      event.target.checked = false
      localStorage.setItem(this.constructor.KEY_ENABLED, "0")
      this.notifyChange()
      this.syncUI()
      return
    }

    if (Notification.permission === "granted") {
      localStorage.setItem(this.constructor.KEY_ENABLED, "1")
      this.notifyChange()
      this.syncUI()
      return
    }

    Notification.requestPermission()
      .then((perm) => {
        localStorage.setItem(this.constructor.KEY_ENABLED, perm === "granted" ? "1" : "0")
      })
      .catch(() => {
        localStorage.setItem(this.constructor.KEY_ENABLED, "0")
      })
      .finally(() => {
        this.notifyChange()
        this.syncUI()
      })
  }

  toggleCategoria(event) {
    const cat = event.currentTarget.dataset.category
    const isChecked = event.currentTarget.checked
    let allowed = this.allowedCategories

    if (isChecked) {
      if (!allowed.includes(cat)) allowed.push(cat)
    } else {
      allowed = allowed.filter((c) => c !== cat)
    }

    localStorage.setItem(this.constructor.KEY_CATEGORIES, JSON.stringify(allowed))
    this.notifyChange()
  }

  testarNotificacao(event) {
    event?.preventDefault()

    if (!("Notification" in window)) {
      alert("Seu navegador não possui suporte a Web Notifications.")
      return
    }

    if (Notification.permission !== "granted") {
      Notification.requestPermission().then((perm) => {
        if (perm === "granted") {
          localStorage.setItem(this.constructor.KEY_ENABLED, "1")
          this.notifyChange()
          this.syncUI()
          this.dispararNotificacaoExemplo()
        } else {
          this.updateHint()
        }
      })
    } else {
      localStorage.setItem(this.constructor.KEY_ENABLED, "1")
      this.notifyChange()
      this.syncUI()
      this.dispararNotificacaoExemplo()
    }
  }

  dispararNotificacaoExemplo() {
    try {
      const notif = new Notification("iWind - Notificação de Teste", {
        body: "Notificações do navegador configuradas com sucesso!",
        icon: "/favicon-32x32.png",
        badge: "/favicon-16x16.png",
        tag: "iwind-teste-notificacao"
      })
      notif.onclick = () => {
        window.focus()
        notif.close()
      }
    } catch (e) {
      console.warn("Erro ao emitir notificação de teste:", e)
    }
  }

  togglePainel(event) {
    event?.preventDefault()
    if (this.hasPanelTarget) {
      const isHidden = this.panelTarget.classList.contains("d-none")
      this.panelTarget.classList.toggle("d-none", !isHidden)

      if (this.hasPanelToggleBtnTarget) {
        this.panelToggleBtnTarget.classList.toggle("active", isHidden)
        this.panelToggleBtnTarget.setAttribute("aria-expanded", isHidden ? "true" : "false")
      }
    }
  }

  granted() {
    return "Notification" in window && Notification.permission === "granted"
  }

  notifyChange() {
    window.dispatchEvent(new CustomEvent("notif-pref-changed"))
  }

  updateHint() {
    const isEnabled = localStorage.getItem(this.constructor.KEY_ENABLED) === "1" && this.granted()

    if (!("Notification" in window)) {
      this.setHint("Este navegador não suporta notificações.")
      this.setBadge("Indisponíveis", "secondary")
    } else if (isEnabled) {
      this.setHint("Ativadas enquanto o app estiver aberto neste dispositivo.")
      this.setBadge("Ativadas", "success")
    } else if ("Notification" in window && Notification.permission === "denied") {
      this.setHint("Bloqueadas nas configurações do navegador.")
      this.setBadge("Bloqueadas", "danger")
    } else {
      this.setHint("Desativadas neste dispositivo.")
      this.setBadge("Desativadas", "secondary")
    }
  }

  setHint(txt) {
    if (this.hasHintTarget) {
      this.hintTargets.forEach((h) => {
        h.textContent = txt
      })
    }
  }

  setBadge(txt, variant) {
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTargets.forEach((b) => {
        b.textContent = txt
        b.className = `badge bg-${variant}-subtle text-${variant} border border-${variant}-subtle`
      })
    }
  }
}
